import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Award, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import Layout from "../components/layout/Layout";

// Values/pillars
const values = [
  {
    icon: Heart,
    title: "Personal Touch",
    description:
      "Every customer is family. We take time to understand your needs and preferences.",
  },
  {
    icon: Award,
    title: "Quality First",
    description:
      "We curate only the finest fabrics and craftsmanship for our discerning clientele.",
  },
  {
    icon: Users,
    title: "Generational Trust",
    description:
      "Families have trusted us for three generations. That trust is our greatest honor.",
  },
  {
    icon: Sparkles,
    title: "Timeless Style",
    description:
      "Fashion trends come and go, but elegance is eternal. We help you find your timeless look.",
  },
];

// Timeline milestones
const milestones = [
  {
    year: "1994",
    title: "The Beginning",
    description:
      "Anand Fashion opens its doors with a vision to bring quality fashion to every family.",
  },
  {
    year: "2004",
    title: "Growing Trust",
    description:
      "A decade of service sees us become the go-to destination for wedding attire in the region.",
  },
  {
    year: "2014",
    title: "Expanding Horizons",
    description:
      "Our showroom expands to offer contemporary and fusion wear alongside traditional collections.",
  },
  {
    year: "2024",
    title: "Three Decades Strong",
    description:
      "Celebrating 30 years of dressing families for their most cherished moments.",
  },
];

export const About = () => {
  // Intersection Observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const revealElements = document.querySelectorAll(".reveal-section");
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-subtle">
        <div className="container-heritage text-center">
          <div className="max-w-3xl mx-auto stagger-children">
            <span className="label-uppercase mb-4 block">Our Story</span>
            <h1 className="heading-display text-foreground mb-6">
              A Legacy Built on Trust
            </h1>
            <div className="flex justify-center mb-6">
              <div className="line-gold" />
            </div>
            <p className="body-large max-w-2xl mx-auto">
              For over three decades, Anand Fashion has been more than a
              showroom — we've been a trusted partner in celebrating life's most
              cherished moments. Anand Fashion is a well-known clothing store in
              Udaipur offering a wide range of men’s wear, women’s wear and
              premium clothing. Located in Bhopalpura near Gattani Hospital, our
              store has been serving local families for over a decade with
              quality fabrics and latest fashion trends. From stylish shirts,
              jeans and winter wear for men to elegant suits, kurtis and co-ord
              sets for women, Anand Fashion is a trusted choice for everyday
              wear as well as special occasions. Our premium collection includes
              blazers, sherwani and party wear suitable for weddings and
              celebrations in Udaipur.
            </p>
          </div>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="section-padding">
        <div className="container-heritage">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="reveal-section">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden rounded-sm">
                  <img
                    src="/anandfashionshop.png"
                    alt="Anand Fashion Showroom Interior"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative accent */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-gold/30 rounded-sm hidden lg:block" />
              </div>
            </div>

            {/* Content */}
            <div className="reveal-section">
              <h2 className="heading-section text-foreground mb-6">
                Where Every Customer is Family
              </h2>
              <div className="line-gold-short mb-8" />
              <div className="space-y-6">
                <p className="body-large">
                  When Anand Fashion first opened its doors in 1994, it was with
                  a simple belief: that quality fashion should be accessible to
                  every family, and that every customer deserves personal
                  attention and respect.
                </p>
                <p className="body-large">
                  Over the years, this philosophy has remained unchanged. While
                  our Branded collection has grown and evolved with the times,
                  our commitment to personal service and genuine relationships
                  has only deepened.
                </p>
                <p className="body-large">
                  Today, we're proud to dress multiple generations of the same
                  families — from grandparents to grandchildren — for weddings,
                  festivals, and everyday elegance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-secondary/30">
        <div className="container-heritage">
          {/* Section Header */}
          <div className="text-center mb-16 reveal-section">
            <span className="label-uppercase mb-4 block">
              What We Stand For
            </span>
            <h2 className="heading-section text-foreground mb-6">Our Values</h2>
            <div className="flex justify-center">
              <div className="line-gold" />
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="reveal-section text-center p-6"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/10 text-gold-dark mb-6">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-3">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding">
        <div className="container-heritage">
          {/* Section Header */}
          <div className="text-center mb-16 reveal-section">
            <span className="label-uppercase mb-4 block">Our Journey</span>
            <h2 className="heading-section text-foreground mb-6">
              30 Years of Excellence
            </h2>
            <div className="flex justify-center">
              <div className="line-gold" />
            </div>
          </div>

          {/* Timeline */}
          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="reveal-section relative pl-8 pb-12 last:pb-0 border-l border-border"
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                {/* Timeline dot */}
                <div className="absolute left-0 top-0 -translate-x-1/2 w-3 h-3 rounded-full bg-gold" />

                {/* Content */}
                <div className="ml-6">
                  <span className="text-gold-dark font-serif text-lg">
                    {milestone.year}
                  </span>
                  <h3 className="font-serif text-xl text-foreground mt-1 mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {milestone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding-sm bg-gradient-warm">
        <div className="container-heritage">
          <div className="max-w-3xl mx-auto text-center reveal-section">
            <blockquote className="relative">
              <span className="absolute -top-4 -left-4 text-6xl text-gold/20 font-serif">
                "
              </span>
              <p className="font-serif text-xl md:text-2xl lg:text-3xl text-foreground italic leading-relaxed">
                Fashion fades, but style is eternal. We don't just sell clothes
                — we help you discover your timeless elegance.
              </p>
              <span className="absolute -bottom-8 -right-4 text-6xl text-gold/20 font-serif">
                "
              </span>
            </blockquote>
            <div className="mt-10">
              <p className="text-muted-foreground">— The Anand Family</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-heritage text-center">
          <div className="reveal-section">
            <h2 className="heading-subsection text-foreground mb-4">
              Experience the Anand Fashion Difference
            </h2>
            <p className="body-large max-w-lg mx-auto mb-8">
              Visit our showroom and let us help you find styles that reflect
              your unique personality and occasion.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/collection">
                <Button className="btn-heritage-filled group min-w-[180px]">
                  View Collection
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="ghost"
                  className="btn-heritage min-w-[180px] group"
                >
                  Visit Us
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
